import { create } from "zustand";
import { persist } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { Diagram, ServiceNode, ServiceConnection, DiagramMetadata } from "../types/diagram.types";
import { diagramsApi, eventsApi, handleApiError } from "../services/api";
import toast from "react-hot-toast";

interface DiagramStore {
  // Current diagram state
  currentDiagram: Diagram | null;
  diagrams: DiagramMetadata[];

  // UI state
  selectedNodeId: string | null;
  selectedConnectionId: string | null;
  hoveredNodeId: string | null;
  highlightedPath: string[];
  highlightedRoutes: string[]; // Track highlighted convoy routes
  isEditMode: boolean;

  // Actions
  createNewDiagram: (name: string, description?: string) => Promise<void>;
  loadDiagram: (id: string) => Promise<void>;
  loadDiagrams: () => Promise<void>;
  saveDiagram: () => Promise<void>;
  deleteDiagram: (id: string) => Promise<void>;
  duplicateDiagram: (id: string, name?: string) => Promise<void>;

  // Node actions
  addNode: (node: Omit<ServiceNode, "id">) => void;
  updateNode: (id: string, updates: Partial<ServiceNode>) => void;
  deleteNode: (id: string) => void;

  // Connection actions
  addConnection: (connection: Omit<ServiceConnection, "id">) => void;
  updateConnection: (id: string, updates: Partial<ServiceConnection>) => void;
  deleteConnection: (id: string) => void;

  // UI actions
  selectNode: (id: string | null) => void;
  selectConnection: (id: string | null) => void;
  setHoveredNode: (id: string | null) => void;
  highlightPath: (nodeId: string) => void;
  clearHighlight: () => void;
  toggleEditMode: () => void;

  // Utility
  exportDiagram: () => string;
  importDiagram: (jsonString: string) => void;
}

// Helper function to log events
const logEvent = async (type: string, diagramId?: string, payload?: any) => {
  try {
    await eventsApi.log({ type, diagramId, payload });
  } catch (error) {
    console.warn("Failed to log event:", error);
  }
};

export const useDiagramStore = create<DiagramStore>()(
  persist(
    (set, get) => ({
      currentDiagram: null,
      diagrams: [],
      selectedNodeId: null,
      selectedConnectionId: null,
      hoveredNodeId: null,
      highlightedPath: [],
      highlightedRoutes: [],
      isEditMode: false,

      createNewDiagram: async (name, description) => {
        try {
          const diagramData = {
            name,
            description,
            nodes: [],
            connections: [],
            version: CURRENT_SCHEMA_VERSION,
          };

          const response = await diagramsApi.create(diagramData);

          const newDiagram: Diagram = {
            id: response.id,
            name,
            description,
            nodes: [],
            connections: [],
            createdAt: new Date(response.createdAt),
            updatedAt: new Date(response.updatedAt),
            version: CURRENT_SCHEMA_VERSION,
          };

          set((state) => ({
            currentDiagram: newDiagram,
            diagrams: [
              ...state.diagrams,
              {
                id: newDiagram.id,
                name: newDiagram.name,
                description: newDiagram.description,
                nodeCount: 0,
                connectionCount: 0,
                createdAt: newDiagram.createdAt,
                updatedAt: newDiagram.updatedAt,
              },
            ],
          }));

          await logEvent("diagram_created", response.id, { name, nodeCount: 0, connectionCount: 0 });
          toast.success(`Diagram "${name}" created successfully`);
        } catch (error) {
          console.error("Failed to create diagram:", error);
          toast.error("Failed to create diagram");
          handleApiError(error);
        }
      },

      loadDiagram: async (id) => {
        try {
          const diagram = await diagramsApi.getById(id);
          if (diagram) {
            const loadedDiagram: Diagram = {
              ...diagram,
              createdAt: new Date(diagram.createdAt),
              updatedAt: new Date(diagram.updatedAt),
            };
            set({ currentDiagram: migrateDiagram(loadedDiagram) });
            await logEvent("diagram_loaded", id);
          }
        } catch (error) {
          console.error("Failed to load diagram:", error);
          toast.error("Failed to load diagram");
          handleApiError(error);
        }
      },

      saveDiagram: async () => {
        const { currentDiagram } = get();
        if (!currentDiagram) return;

        // Ensure version is set before saving
        if (!currentDiagram.version) {
          currentDiagram.version = CURRENT_SCHEMA_VERSION;
        }

        try {
          const diagramToSave = {
            ...currentDiagram,
            createdAt: currentDiagram.createdAt.toISOString(),
            updatedAt: new Date().toISOString(),
          };

          const response = await diagramsApi.update(currentDiagram.id, diagramToSave);

          // Update local state with server response
          const updatedAt = new Date(response.updatedAt);
          set((state) => ({
            currentDiagram: {
              ...state.currentDiagram!,
              updatedAt,
            },
            diagrams: state.diagrams.map((d) =>
              d.id === currentDiagram.id
                ? {
                    ...d,
                    nodeCount: currentDiagram.nodes.length,
                    connectionCount: currentDiagram.connections.length,
                    updatedAt,
                  }
                : d
            ),
          }));

          await logEvent("diagram_saved", currentDiagram.id, {
            nodeCount: currentDiagram.nodes.length,
            connectionCount: currentDiagram.connections.length,
          });
        } catch (error) {
          console.error("Failed to save diagram:", error);
          toast.error("Failed to save diagram");
          handleApiError(error);
        }
      },

      deleteDiagram: async (id) => {
        try {
          const diagramToDelete = get().diagrams.find((d) => d.id === id);
          await diagramsApi.delete(id);

          set((state) => ({
            diagrams: state.diagrams.filter((d) => d.id !== id),
            currentDiagram: state.currentDiagram?.id === id ? null : state.currentDiagram,
          }));

          await logEvent("diagram_deleted", id, { name: diagramToDelete?.name });
          toast.success("Diagram deleted successfully");
        } catch (error) {
          console.error("Failed to delete diagram:", error);
          toast.error("Failed to delete diagram");
          handleApiError(error);
        }
      },

      loadDiagrams: async () => {
        try {
          const response = await diagramsApi.getAll(1, 100); // Load first 100 diagrams
          const diagramMetadata = response.diagrams.map((d) => ({
            id: d.id,
            name: d.name,
            description: d.description,
            nodeCount: d.nodeCount,
            connectionCount: d.connectionCount,
            createdAt: new Date(d.createdAt),
            updatedAt: new Date(d.updatedAt),
            tags: d.tags,
          }));

          set({ diagrams: diagramMetadata });
        } catch (error) {
          console.error("Failed to load diagrams:", error);
          toast.error("Failed to load diagrams");
          handleApiError(error);
        }
      },

      duplicateDiagram: async (id, name) => {
        try {
          const response = await diagramsApi.duplicate(id, name);
          const newMetadata: DiagramMetadata = {
            id: response.id,
            name: response.name,
            description: undefined,
            nodeCount: 0,
            connectionCount: 0,
            createdAt: new Date(response.createdAt),
            updatedAt: new Date(response.createdAt),
          };

          set((state) => ({
            diagrams: [newMetadata, ...state.diagrams],
          }));

          await logEvent("diagram_duplicated", response.id, { originalId: id, newName: response.name });
          toast.success(`Diagram duplicated as "${response.name}"`);
        } catch (error) {
          console.error("Failed to duplicate diagram:", error);
          toast.error("Failed to duplicate diagram");
          handleApiError(error);
        }
      },

      addNode: (node) => {
        const newNode: ServiceNode = {
          ...node,
          id: uuidv4(),
        };

        set((state) => ({
          currentDiagram: state.currentDiagram
            ? {
                ...state.currentDiagram,
                nodes: [...state.currentDiagram.nodes, newNode],
              }
            : null,
        }));

        // autosave
        get()
          .saveDiagram()
          .catch(() => {
            console.warn("Auto-save failed");
          });
      },

      updateNode: (id, updates) => {
        set((state) => ({
          currentDiagram: state.currentDiagram
            ? {
                ...state.currentDiagram,
                nodes: state.currentDiagram.nodes.map((n) => (n.id === id ? { ...n, ...updates } : n)),
              }
            : null,
        }));

        // autosave
        get()
          .saveDiagram()
          .catch(() => {
            console.warn("Auto-save failed");
          });
      },

      deleteNode: (id) => {
        set((state) => ({
          currentDiagram: state.currentDiagram
            ? {
                ...state.currentDiagram,
                nodes: state.currentDiagram.nodes.filter((n) => n.id !== id),
                connections: state.currentDiagram.connections.filter((c) => c.source !== id && c.target !== id),
              }
            : null,
        }));

        // autosave
        get()
          .saveDiagram()
          .catch(() => {
            console.warn("Auto-save failed");
          });
      },

      addConnection: (connection) => {
        const newConnection: ServiceConnection = {
          ...connection,
          id: uuidv4(),
        };

        set((state) => ({
          currentDiagram: state.currentDiagram
            ? {
                ...state.currentDiagram,
                connections: [...state.currentDiagram.connections, newConnection],
              }
            : null,
        }));

        // autosave
        get()
          .saveDiagram()
          .catch(() => {
            console.warn("Auto-save failed");
          });
      },

      updateConnection: (id, updates) => {
        set((state) => ({
          currentDiagram: state.currentDiagram
            ? {
                ...state.currentDiagram,
                connections: state.currentDiagram.connections.map((c) => (c.id === id ? { ...c, ...updates } : c)),
              }
            : null,
        }));

        // autosave
        get()
          .saveDiagram()
          .catch(() => {
            console.warn("Auto-save failed");
          });
      },

      deleteConnection: (id) => {
        set((state) => ({
          currentDiagram: state.currentDiagram
            ? {
                ...state.currentDiagram,
                connections: state.currentDiagram.connections.filter((c) => c.id !== id),
              }
            : null,
        }));

        // autosave
        get()
          .saveDiagram()
          .catch(() => {
            console.warn("Auto-save failed");
          });
      },

      selectNode: (id) => set({ selectedNodeId: id, selectedConnectionId: null }),
      selectConnection: (id) => set({ selectedConnectionId: id, selectedNodeId: null }),
      setHoveredNode: (id) => set({ hoveredNodeId: id }),

      highlightPath: (nodeId) => {
        const { currentDiagram } = get();
        if (!currentDiagram) return;

        const connectedNodes = new Set<string>();
        const highlightedRoutes = new Set<string>();
        const visitedNodes = new Set<string>();

        // Recursive function to trace all paths
        const tracePath = (currentNodeId: string, direction: "forward" | "backward" | "both") => {
          if (visitedNodes.has(currentNodeId)) return;
          visitedNodes.add(currentNodeId);
          connectedNodes.add(currentNodeId);

          // Find all connections for this node
          currentDiagram.connections.forEach((conn) => {
            // For forward tracing (following the flow)
            if ((direction === "forward" || direction === "both") && conn.source === currentNodeId) {
              connectedNodes.add(conn.target);
              // If this connection uses a convoy route, highlight that route
              if (conn.sourceHandle?.startsWith("route-")) {
                const routeId = conn.sourceHandle.replace("route-", "");
                highlightedRoutes.add(routeId);
              }
              tracePath(conn.target, "forward");
            }

            // For backward tracing (going back to source)
            if ((direction === "backward" || direction === "both") && conn.target === currentNodeId) {
              connectedNodes.add(conn.source);
              // If we're tracing back through a convoy, highlight the route
              if (conn.sourceHandle?.startsWith("route-")) {
                const routeId = conn.sourceHandle.replace("route-", "");
                highlightedRoutes.add(routeId);
              }
              tracePath(conn.source, "backward");
            }
          });

          // Note: convoy route highlighting now happens only when traversing
          // connections that explicitly use a route via sourceHandle starting with
          // `route-` (handled in the forward/backward connection iteration above).
        };

        // Start tracing from the hovered node in both directions
        tracePath(nodeId, "both");

        set({
          highlightedPath: Array.from(connectedNodes),
          highlightedRoutes: Array.from(highlightedRoutes),
        });
      },

      clearHighlight: () => set({ highlightedPath: [], highlightedRoutes: [] }),
      toggleEditMode: () => set((state) => ({ isEditMode: !state.isEditMode })),

      exportDiagram: () => {
        const { currentDiagram } = get();
        if (!currentDiagram) return "null";
        const withVersion: Diagram = {
          ...currentDiagram,
          version: currentDiagram.version || CURRENT_SCHEMA_VERSION,
        };
        return JSON.stringify(withVersion, null, 2);
      },

      importDiagram: (jsonString) => {
        try {
          const parsed = JSON.parse(jsonString) as Diagram;
          // Normalize dates if strings, and migrate structure/handles
          const hydrated: Diagram = {
            ...parsed,
            id: uuidv4(),
            createdAt: new Date(parsed.createdAt || Date.now()),
            updatedAt: new Date(parsed.updatedAt || Date.now()),
          };
          const migrated = migrateDiagram(hydrated);

          // Persist imported diagram to backend as a new diagram (POST)
          (async () => {
            try {
              const createPayload = {
                id: migrated.id,
                name: migrated.name,
                description: migrated.description,
                nodes: migrated.nodes,
                connections: migrated.connections,
                tags: migrated.tags,
                version: migrated.version || CURRENT_SCHEMA_VERSION,
              };
              const res = await diagramsApi.create(createPayload);

              // Use server-confirmed id and timestamps
              const persisted: Diagram = {
                ...migrated,
                id: res.id,
                createdAt: new Date(res.createdAt),
                updatedAt: new Date(res.updatedAt),
              };

              set((state) => ({
                currentDiagram: persisted,
                diagrams: [
                  ...state.diagrams,
                  {
                    id: persisted.id,
                    name: persisted.name,
                    description: persisted.description,
                    nodeCount: persisted.nodes.length,
                    connectionCount: persisted.connections.length,
                    createdAt: persisted.createdAt,
                    updatedAt: persisted.updatedAt,
                  },
                ],
              }));

              toast.success("Diagram imported successfully");
            } catch (err) {
              console.error("Failed to import diagram (server):", err);
              toast.error("Failed to import diagram");
              handleApiError(err);
            }
          })();
        } catch (error) {
          console.error("Failed to import diagram:", error);
        }
      },
    }),
    {
      name: "diagram-store",
      partialize: (state) => ({
        diagrams: state.diagrams,
      }),
    }
  )
);

// ---------- Migration utilities ----------
const CURRENT_SCHEMA_VERSION = 2;

function migrateDiagram(input: Diagram): Diagram {
  const version = input.version ?? 1;
  // Hydrate dates if they are strings
  const hydratedDates: Diagram = {
    ...input,
    createdAt: input.createdAt instanceof Date ? input.createdAt : new Date(input.createdAt),
    updatedAt: input.updatedAt instanceof Date ? input.updatedAt : new Date(input.updatedAt),
  };
  let diagram: Diagram = { ...hydratedDates };

  if (version < 2) {
    // v1 -> v2: multi-side handle normalization
    // Map generic handles to explicit side IDs
    diagram = {
      ...diagram,
      connections: diagram.connections.map((c) => normalizeConnectionHandles(c, diagram.nodes)),
      version: 2,
    };
  }

  // Future migrations go here

  return diagram;
}

function normalizeConnectionHandles(conn: ServiceConnection, nodes?: ServiceNode[]): ServiceConnection {
  // Preserve convoy route source handle
  const isConvoyRoute = conn.sourceHandle?.startsWith("route-");
  let sourceHandle = conn.sourceHandle;
  let targetHandle = conn.targetHandle;

  if (!isConvoyRoute) {
    // Map common legacy values to new handle IDs
    sourceHandle = mapLegacySourceHandle(sourceHandle);
  }
  targetHandle = mapLegacyTargetHandle(targetHandle);

  // If handles are missing, compute based on positions
  if (nodes && (!sourceHandle || !targetHandle)) {
    const sourceNode = nodes.find((n) => n.id === conn.source);
    const targetNode = nodes.find((n) => n.id === conn.target);
    if (sourceNode && targetNode) {
      const dx = (targetNode.position?.x ?? 0) - (sourceNode.position?.x ?? 0);
      const dy = (targetNode.position?.y ?? 0) - (sourceNode.position?.y ?? 0);
      if (Math.abs(dx) >= Math.abs(dy)) {
        sourceHandle = sourceHandle || (dx >= 0 ? "source-right" : "source-left");
        targetHandle = targetHandle || (dx >= 0 ? "target-left" : "target-right");
      } else {
        sourceHandle = sourceHandle || (dy >= 0 ? "source-bottom" : "source-top");
        targetHandle = targetHandle || (dy >= 0 ? "target-top" : "target-bottom");
      }
    }
  }

  return { ...conn, sourceHandle, targetHandle };
}

function mapLegacySourceHandle(h?: string): string | undefined {
  if (!h) return undefined;
  switch (h) {
    case "left":
    case "source-left":
      return "source-left";
    case "right":
    case "source-right":
      return "source-right";
    case "top":
    case "source-top":
      return "source-top";
    case "bottom":
    case "source-bottom":
      return "source-bottom";
    default:
      return h;
  }
}

function mapLegacyTargetHandle(h?: string): string | undefined {
  if (!h) return undefined;
  switch (h) {
    case "left":
    case "target-left":
      return "target-left";
    case "right":
    case "target-right":
      return "target-right";
    case "top":
    case "target-top":
      return "target-top";
    case "bottom":
    case "target-bottom":
      return "target-bottom";
    default:
      return h;
  }
}
